import { all, call, put, takeLatest, takeLeading } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateBadgeRequest,
  CreateBadgeResponse,
  DeleteBadgeRequest,
  DeleteBadgeResponse,
  UpdateBadgeRequest,
  UpdateBadgeResponse,
  ViewBadgesResponse
} from './badge.types';

import { CREATING_BADGE, DELETING_BADGE, RETRIEVING_BADGES, UPDATING_BADGE } from './badge.constants';

import { requestCreateBadge, requestDeleteBadge, requestUpdateBadge, requestViewBadges } from './badge.actions';

import {
  getCreateBadgeRequest,
  getDeleteBadgeRequest,
  getUpdateBadgeRequest,
  getViewBadgesRequest
} from './badge.endpoints';

import { createBadge, deleteBadge, setBadges, updateBadge } from '../../slices/badge/badge.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateBadgeRequest({ payload }: SagaPayload<CreateBadgeRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_BADGE));

    const [endpoint, options] = getCreateBadgeRequest(requestData);

    const { data }: AxiosResponse<CreateBadgeResponse> = yield call(axios, endpoint, options);

    yield put(createBadge(data.badge));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_BADGE));
  }
}

export function* watchForCreateBadgeRequest() {
  yield takeLatest(requestCreateBadge.type, performCreateBadgeRequest);
}

export function* performViewBadgesRequest() {
  try {
    yield put(addSpinner(RETRIEVING_BADGES));

    const [endpoint, options] = getViewBadgesRequest();

    const { data }: AxiosResponse<ViewBadgesResponse> = yield call(axios, endpoint, options);

    yield put(setBadges(data.badges));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_BADGES));
  }
}

export function* watchForViewBadgesRequest() {
  yield takeLeading(requestViewBadges.type, performViewBadgesRequest);
}

export function* performUpdateBadgeRequest({ payload }: SagaPayload<UpdateBadgeRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_BADGE));

    const [endpoint, options] = getUpdateBadgeRequest(requestData);

    const { data }: AxiosResponse<UpdateBadgeResponse> = yield call(axios, endpoint, options);

    yield put(updateBadge(data.badge));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_BADGE));
  }
}

export function* watchForUpdateBadgeRequest() {
  yield takeLatest(requestUpdateBadge.type, performUpdateBadgeRequest);
}

export function* performDeleteBadgeRequest({ payload }: SagaPayload<DeleteBadgeRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_BADGE));

    const [endpoint, options] = getDeleteBadgeRequest(requestData);

    const { data }: AxiosResponse<DeleteBadgeResponse> = yield call(axios, endpoint, options);

    yield put(deleteBadge(requestData.badgeExternalReference));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_BADGE));
  }
}

export function* watchForDeleteBadgeRequest() {
  yield takeLatest(requestDeleteBadge.type, performDeleteBadgeRequest);
}

export default function* badgeSaga() {
  yield all([
    watchForCreateBadgeRequest(),
    watchForViewBadgesRequest(),
    watchForUpdateBadgeRequest(),
    watchForDeleteBadgeRequest()
  ]);
}
